import { mergeAttributes, Node, type Range } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { parseVariableTokens } from "../variable-token";
import { canInsertNodeAt } from "../variable-mention/variable-mention.utilities";
import {
    TEMPLATE_MENTION_DATA_ATTRIBUTE,
    TEMPLATE_MENTION_NODE_NAME,
    TEMPLATE_MENTION_TOKEN_ATTRIBUTE,
} from "./template-mention.constants";
import type { ITemplateMentionAttributes } from "./template-mention.types";
import {
    formatTemplateCommand,
    isValidTemplate,
    serialiseTemplateToken,
    templateFromNode,
    templateFromVariableToken,
} from "./template-mention.utilities";
import { TemplateMentionView } from "./template-mention.view";

/**
 * Tiptap node for an Ask Spotlight template: an inline chip that stands for one template, drawn as its `/command`. It
 * serialises to a template token in the variable token grammar,
 * `{{template:(aiChatReportTemplateId=<id>)|Template|<command>}}`, so the message the agent receives and stores carries
 * the template's id. It round-trips through HTML on a single `data-token` attribute. One Backspace with the caret after
 * the chip removes the whole chip.
 */
export const TemplateMention = Node.create({
    // One indivisible unit inside a line of text: selectable as a whole, never entered or dragged.
    name: TEMPLATE_MENTION_NODE_NAME,
    group: "inline",
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    marks: "",

    // Neither attribute is written to or read from HTML on its own: `data-token` carries both.
    addAttributes() {
        const tokenOnly = { renderHTML: () => ({}), parseHTML: () => null };

        return {
            aiChatReportTemplateId: { default: null, ...tokenOnly },
            command: { default: null, ...tokenOnly },
        };
    },

    // HTML in: a `<span data-template-mention data-token="…">` becomes a chip when its token is exactly one template.
    parseHTML() {
        return [
            {
                tag: `span[${TEMPLATE_MENTION_DATA_ATTRIBUTE}]`,
                getAttrs: (element) => {
                    const token =
                        typeof element === "string" ? null : element.getAttribute(TEMPLATE_MENTION_TOKEN_ATTRIBUTE);
                    const segments = token === null ? [] : parseVariableTokens(token);
                    const template =
                        segments.length === 1 && segments[0].type === "variable"
                            ? templateFromVariableToken(segments[0].variable)
                            : null;

                    return template ?? false;
                },
            },
        ];
    },

    // HTML out: the same span, with the chip text as its text.
    renderHTML({ node, HTMLAttributes }) {
        const template = templateFromNode(node);

        return [
            "span",
            mergeAttributes({ [TEMPLATE_MENTION_DATA_ATTRIBUTE]: "" }, HTMLAttributes, {
                [TEMPLATE_MENTION_TOKEN_ATTRIBUTE]: serialiseTemplateToken(template),
            }),
            formatTemplateCommand(template.command),
        ];
    },

    // Plain text out: `editor.getText()` gives the token, which is what the agent receives.
    renderText({ node }) {
        return serialiseTemplateToken(templateFromNode(node));
    },

    addNodeView() {
        return ReactNodeViewRenderer(TemplateMentionView);
    },

    addCommands() {
        return {
            insertTemplateMention:
                (template: ITemplateMentionAttributes, range?: Range) =>
                ({ commands, state }) => {
                    if (!isValidTemplate(template)) {
                        return false;
                    }

                    // Replace the given range (e.g. the typed `/query`), or else the selection.
                    const { from, to } = range ?? state.selection;

                    if (from < 0 || to > state.doc.content.size || from > to) {
                        return false;
                    }

                    if (!canInsertNodeAt(state, state.schema.nodes[this.name], from)) {
                        return false;
                    }

                    // Reuse a space that already follows, so the chip never ends up with two after it.
                    const nodeAfter = state.doc.resolve(to).nodeAfter;
                    const followedBySpace = nodeAfter?.isText === true && nodeAfter.text?.startsWith(" ") === true;
                    const { aiChatReportTemplateId, command } = template;

                    return commands.insertContentAt({ from, to: followedBySpace ? to + 1 : to }, [
                        { type: this.name, attrs: { aiChatReportTemplateId, command } },
                        { type: "text", text: " " },
                    ]);
                },
        };
    },

    // The chip is one unit, so Backspace right after it removes all of it in one press.
    addKeyboardShortcuts() {
        return {
            Backspace: () => {
                const { selection } = this.editor.state;
                const nodeBefore = selection.$from.nodeBefore;

                if (!selection.empty || nodeBefore?.type.name !== this.name) {
                    return false;
                }

                const to = selection.$from.pos;

                return this.editor.commands.deleteRange({ from: to - nodeBefore.nodeSize, to });
            },
        };
    },
});
